import { memo } from "react";

import { ChevronRight } from "components/icons/generated";
import RunPriorityMenuItems from "components/Run/elements/RunPriorityMenuItems";
import DropdownMenuDivider from "ds/components/DropdownMenu/Divider";
import DropdownMenuEllipsis from "ds/components/DropdownMenu/Ellipsis";
import DropdownMenuItem from "ds/components/DropdownMenu/Item";
import DropdownSubmenu from "ds/components/DropdownMenu/Submenu";
import Icon from "ds/components/Icon";
import { RUN_PRIORITY_PRESETS } from "shared/Run/priorityPresets";
import { RunPriorityPreset } from "types/generated";
import { AccessResult } from "utils/access";

import useDiscardRun from "./useDiscardRun";
import styles from "./styles.module.css";

type WorkerPoolQueuedRunsListItemDropdownProps = {
  runId: string;
  stackId: string;
  priorityPreset: RunPriorityPreset;
  onSetPriority: (preset: RunPriorityPreset) => void;
  isPublic?: boolean;
  canDiscard: AccessResult;
  canPrioritize: AccessResult;
};

const WorkerPoolQueuedRunsListItemDropdown = ({
  stackId,
  runId,
  priorityPreset,
  onSetPriority,
  isPublic,
  canDiscard,
  canPrioritize,
}: WorkerPoolQueuedRunsListItemDropdownProps) => {
  const { discardRun, discarding } = useDiscardRun();

  const handleDiscardRun = () => discardRun(stackId, runId);

  // A bare `{ result: false }` means hide, `{ result: false, message }` means show disabled.
  const showPriority = !isPublic && (canPrioritize.result || !!canPrioritize.message);
  const showDiscard = canDiscard.result || !!canDiscard.message;

  if (!showPriority && !showDiscard) {
    return null;
  }

  return (
    <DropdownMenuEllipsis tooltip="Run actions">
      {showPriority && (
        <DropdownSubmenu
          placement="right top"
          triggerComponent={
            <DropdownMenuItem className={styles.priorityTrigger}>
              {RUN_PRIORITY_PRESETS[priorityPreset].label}
              <Icon src={ChevronRight} />
            </DropdownMenuItem>
          }
        >
          <RunPriorityMenuItems
            current={priorityPreset}
            onSelect={onSetPriority}
            canPrioritize={canPrioritize}
          />
        </DropdownSubmenu>
      )}
      {showPriority && showDiscard && <DropdownMenuDivider />}
      {showDiscard && (
        <DropdownMenuItem
          isDisabled={!canDiscard.result}
          tooltip={canDiscard.message}
          onAction={handleDiscardRun}
          loading={discarding}
        >
          Discard
        </DropdownMenuItem>
      )}
    </DropdownMenuEllipsis>
  );
};

export default memo(WorkerPoolQueuedRunsListItemDropdown);
